import { useId, useState, type ReactNode } from 'react';
import { ensureMyPlayer } from '../../lib/api/myPlayer';
import { useT } from '../../i18n';
import { safeId } from '../ui/labelledControl';
import { BigButton } from './mobileUi';

// ─────────────────────────────────────────────────────────────────────
// THE STATES THE APP SHOWS IN PLACE OF A SCREEN — never in place of the app.
//
// They were full-screen early returns: the club's load and the identity read
// each replaced the whole app, header and tab bar included. That made sense
// while a club was the way in; it does not now that the app is the player's
// home (owner, 2026-10-16). The shell stays — header, 📍 selector, tabs — and
// these fill the content area, so a club that failed to load can be switched
// away from and the personal tabs stay reachable.
//
// THE SENTENCES ARE UNCHANGED, and so is their order (#800, #801 review):
//
//   «we could not load the club»   retry the CLUB, or choose another
//   «we could not ask who you are» retry the IDENTITY read
//   «you have no profile yet»      a state, not a failure — create one
//
// None may stand in for another. In particular a club still loading must not
// produce «no profile», which is a false statement about the account.
// ─────────────────────────────────────────────────────────────────────

/**
 * A panel, and — where one is passed — the way out of the app.
 *
 * ── A STATE THAT FILLS PROFIL MUST STILL OFFER «LOGG UT» ─────────
 *
 * Sign-out lives in Profil, and these states fill Profil too. Without this, a
 * person with no player profile — or whose club or identity did not load — had
 * no way to sign out at all in the native app, which has no header.
 */
function Panel({
  children,
  onSignOut = null,
}: {
  children: ReactNode;
  onSignOut?: (() => void) | null;
}) {
  const t = useT();
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center gap-3">
      {children}
      {onSignOut && (
        <button type="button" className="btn-ghost text-xs" onClick={onSignOut}>
          {t('mobile.profile.signOut')}
        </button>
      )}
    </div>
  );
}

export function ClubLoadState({
  status,
  onRetry,
  onChooseClub = null,
  onSignOut = null,
}: {
  status: 'loading' | 'failed';
  onRetry?: () => void;
  /** Open the 📍 selector. Null where the club cannot change. */
  onChooseClub?: (() => void) | null;
  /** Passed where this panel stands in for Profil. */
  onSignOut?: (() => void) | null;
}) {
  const t = useT();
  return (
    <Panel onSignOut={onSignOut}>
      {status === 'failed' ? (
        <>
          <p className="font-display text-lg font-semibold">{t('mobile.club.failedTitle')}</p>
          <p className="text-sm text-ink-3">{t('mobile.club.failedBody')}</p>
          <button type="button" className="btn-primary" onClick={onRetry}>
            {t('mobile.identity.retry')}
          </button>
        </>
      ) : (
        <p className="text-sm text-ink-3">{t('mobile.club.loading')}</p>
      )}
      {onChooseClub && (
        <button type="button" className="btn-ghost text-xs" onClick={onChooseClub}>
          {t('mobile.clubContext.chooseOther')}
        </button>
      )}
    </Panel>
  );
}

export function IdentityState({
  failed,
  onRetry,
  canCreate,
  onSignOut = null,
  provisionPlayer = ensureMyPlayer,
}: {
  /** True when the read did not answer; false when it answered «none». */
  failed: boolean;
  onRetry: () => void;
  /**
   * Whether this person can make a profile here: an ACCOUNT on the player
   * surface. A legacy member session names a roster row the club made; it
   * has nothing to create.
   */
  canCreate: boolean;
  /** Passed where this panel stands in for Profil. */
  onSignOut?: (() => void) | null;
  /** Injected in tests; the real one is `POST /me/player`. */
  provisionPlayer?: typeof ensureMyPlayer;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Only an empty name is the FIELD's fault; a failed create is described by
  // the same message but does not make what was typed invalid.
  const [nameInvalid, setNameInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const auto = useId();
  const errorId = safeId('identity-error', auto);

  const create = async () => {
    if (busy) return;
    const given = name.trim();
    if (given === '') {
      setError(t('mobile.identity.nameRequired'));
      setNameInvalid(true);
      return;
    }
    setNameInvalid(false);
    setBusy(true);
    const outcome = await provisionPlayer(given);
    setBusy(false);
    if (outcome.code !== null) {
      setError(t('mobile.identity.createFailed'));
      return;
    }
    // Ask again rather than adopt: `/me/player` is the authority on who this
    // is, and the read that follows is the same one a reload makes.
    onRetry();
  };

  return (
    <Panel onSignOut={onSignOut}>
      <p className="font-display text-lg font-semibold">
        {failed ? t('mobile.identity.failedTitle') : t('mobile.identity.noneTitle')}
      </p>
      <p className="text-sm text-ink-3">
        {failed
          ? t('mobile.identity.failedBody')
          : canCreate
            ? t('mobile.identity.noneBody')
            : t('mobile.identity.noneMemberBody')}
      </p>
      {failed && (
        <button type="button" className="btn-primary" onClick={onRetry}>
          {t('mobile.identity.retry')}
        </button>
      )}
      {!failed && canCreate && (
        <div className="w-full max-w-xs text-left space-y-2">
          <label className="block">
            <span className="block font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
              {t('mobile.identity.nameLabel')}
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              className="w-full rounded-xl px-3 py-2 text-[14px] text-ink"
              style={{ background: '#eef0ea' }}
              disabled={busy}
              // The message below is announced when it appears (`role="alert"`)
              // and read again with the field (#900).
              aria-invalid={nameInvalid || undefined}
              aria-describedby={error ? errorId : undefined}
            />
          </label>
          {error && (
            <p id={errorId} role="alert" className="text-[12px] text-danger">
              {error}
            </p>
          )}
          <BigButton onClick={() => void create()} disabled={busy}>
            {busy ? t('mobile.identity.creating') : t('mobile.identity.create')}
          </BigButton>
        </div>
      )}
    </Panel>
  );
}
